import { useEffect, useState } from "react";
import { X, Zap } from "lucide-react";
import {
  noteBonusClicked,
  noteBonusDismissed,
  recordBonusEvent,
  type SmartBonusOffer,
} from "@/lib/betting/smartBonusEngine";
import { trackEvent } from "@/lib/audit";

interface Props {
  offer: SmartBonusOffer;
  onClose: () => void;
}

export function SmartBonusPopup({ offer, onClose }: Props) {
  const [remaining, setRemaining] = useState(offer.countdownSeconds);

  useEffect(() => {
    void trackEvent("smart_bonus_viewed", { bonus_id: offer.id });
  }, [offer.id]);

  useEffect(() => {
    const iv = setInterval(() => setRemaining((r) => r - 1), 1000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (remaining <= 0) {
      void recordBonusEvent(offer, { expired: true });
      void trackEvent("smart_bonus_expired", { bonus_id: offer.id });
      onClose();
    }
  }, [remaining, offer, onClose]);

  const handleDismiss = () => {
    noteBonusDismissed();
    void trackEvent("smart_bonus_dismissed", { bonus_id: offer.id });
    onClose();
  };

  const handleClick = () => {
    noteBonusClicked();
    void recordBonusEvent(offer, { clicked: true });
    void trackEvent("smart_bonus_clicked", {
      bonus_id: offer.id,
      simulated_odds: offer.simulatedOdds,
    });
    onClose();
  };

  const pct = Math.max(0, (remaining / offer.countdownSeconds) * 100);
  const circumference = 2 * Math.PI * 18;

  return (
    <div
      role="dialog"
      aria-label="Smart bonus simulado"
      className="fixed bottom-24 left-1/2 z-40 w-[92%] max-w-sm -translate-x-1/2 rounded-2xl border border-[oklch(0.82_0.24_145)]/40 bg-background/95 p-4 shadow-[0_10px_40px_-5px_oklch(0.82_0.24_145_/_0.35)] backdrop-blur lg:bottom-6 lg:left-auto lg:right-6 lg:translate-x-0"
    >
      <button
        onClick={handleDismiss}
        aria-label="Cerrar bono"
        className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-accent"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex items-start gap-3">
        <div className="relative h-12 w-12 shrink-0">
          <svg viewBox="0 0 44 44" className="h-12 w-12 -rotate-90">
            <circle cx="22" cy="22" r="18" stroke="oklch(0.3 0 0)" strokeWidth="3" fill="none" />
            <circle
              cx="22"
              cy="22"
              r="18"
              stroke="oklch(0.82 0.24 145)"
              strokeWidth="3"
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={circumference * (1 - pct / 100)}
              strokeLinecap="round"
              style={{ transition: "stroke-dashoffset 1s linear" }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center text-[10px] font-black text-[oklch(0.82_0.24_145)]">
            {Math.max(0, remaining)}s
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <Zap className="h-3.5 w-3.5 text-[oklch(0.82_0.24_145)]" />
            <div className="text-[13px] font-black tracking-tight">{offer.title}</div>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-1">
            <span className="rounded-full border border-[oklch(0.82_0.24_145)]/40 bg-[oklch(0.82_0.24_145)]/10 px-1.5 py-0.5 text-[9px] font-bold tracking-wider text-[oklch(0.82_0.24_145)]">
              SIMULADO
            </span>
            <span className="text-[10px] font-bold text-muted-foreground">
              Cuota sim. {offer.simulatedOdds}
            </span>
          </div>
          <p className="mt-1.5 text-[11px] leading-snug text-muted-foreground">
            {offer.description}
          </p>
          <button
            onClick={handleClick}
            className="mt-2 w-full rounded-lg bg-gradient-to-r from-[oklch(0.82_0.24_145)] to-[oklch(0.7_0.22_145)] py-2 text-[12px] font-bold text-background transition hover:opacity-90 active:scale-95"
          >
            Ver oferta
          </button>
          <p className="mt-1.5 text-[9px] leading-snug text-muted-foreground">
            {offer.disclaimer} Juega con responsabilidad.
          </p>
        </div>
      </div>
    </div>
  );
}